import React, { useCallback, useContext, useEffect, useState } from 'react'
import { Breadcrumbs } from '@/components/ui/breadcrumb'
import { GridColumn as Column, Grid, GridCellProps } from '@progress/kendo-react-grid'
import { PermissionsEnum } from 'api/Identity/Types/PermissionsEnum'
import { PageData } from 'api/Pagination/Types'
import { UploadFileHistoryOverviewDto } from 'api/Wallet/Types/UploadFileHistoryOverviewDto'
import { Pager } from 'components/components/Pager'
import { Modal } from 'components/CustomModal'
import { Spinner } from 'components/Spinner'
import { createGridState, useGridFilter, useGridState, useGridStatusState } from 'features/DataGrid'
import { isGoBack } from 'lib/react-router-dom'
import { CompositeFilterDescriptor, GridFilterActionState } from 'store/grid'
import { useApiClient } from 'utils/api/ApiClient'
import { WalletsApiClient } from 'utils/api/walletsApiClient'
import { AuthSessionContext } from 'utils/AuthContext'
import { links } from 'utils/featureToggles'
import { hasPermission } from 'utils/permissionsValidator'
import { buildQuery } from 'utils/QueryHelpers'
import { TableWithExport } from 'utils/table/withExport'
import { TableStateContext, TableStateContextData } from 'utils/TableStateContext'

import { transactionBulkTransactionFileUploadHistory } from './transactionBulkTransactionFileUploadHistoryColumns'
import { UploadTransactionHistoryPage } from './UploadTransactionHistoryPage'

const TransactionBulkTransactionFileUploadHistoryOverview = (): JSX.Element => {
  const [auth] = useContext(AuthSessionContext)
  const [tableData, setTableData] = useState<TableStateContextData>({ skipCount: true })

  const walletsApiClient = useApiClient(WalletsApiClient)
  const permissions = auth?.permissions || []
  const [uploadHistoryModal, setUploadHistoryModal] = useState<{
    visible: boolean
    historyId?: string
  }>({
    visible: false,
  })

  const [transactions, setTransactions] = useState<UploadFileHistoryOverviewDto[]>([])

  const { gridStatusState, gridStatusActions } = useGridStatusState()
  const { gridState, gridActions } = useGridState()
  const { selectFilterForGoBackHistory } = useGridFilter(gridStatusActions)

  useEffect(() => {
    ;(async () => {
      await fetchGrid(
        async (
          gridFilterState: GridFilterActionState
        ): Promise<PageData<UploadFileHistoryOverviewDto[]>> => {
          const searchQuery = getQuery(gridFilterState.filter)
          const response = await walletsApiClient.getFileUploadHistoryTransactions({
            search: searchQuery,
            pageIndex: gridFilterState.pageIndex,
            pageSize: gridFilterState.pageSize,
            skipCount: tableData?.skipCount !== false,
          })
          if (response?.items) {
            setTransactions(response.items)
          }

          return response
        }
      )
    })()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const fetchGrid = async (
    callback: (
      gridFilterState: GridFilterActionState
    ) => Promise<PageData<UploadFileHistoryOverviewDto[]>>
  ) => {
    gridStatusActions.setLoading(true)

    const gridFilterState = selectFilterForGoBackHistory(
      createGridState({ skip: 0 }, gridState, { isGoBack })
    )
    const response = await callback(gridFilterState)
    if (response?.totalResults >= 0) {
      const total = response.totalResults

      gridActions.setGridFilters({
        total,
      })
    }

    gridStatusActions.setLoading(false)
  }

  const getQuery = useCallback(
    (filter?: CompositeFilterDescriptor) => {
      const gridFilter = filter ?? gridState.filter
      return buildQuery(gridFilter) ?? null
    },
    [gridState.filter]
  )

  const handlePageSizeChange = async (pageSize: number) => {
    await gridActions.setGridFilters({
      ...gridState,
      pageSize,
    })

    const searchQuery = getQuery()

    const response = await walletsApiClient.getFileUploadHistoryTransactions({
      search: searchQuery,
      pageIndex: 1,
      pageSize,
      skipCount: tableData?.skipCount !== false,
    })
    await gridActions.setGridFilters({
      ...gridState,
      pageIndex: 1,
      take: pageSize,
      total: response.totalResults,
    })
    if (response?.items) {
      setTransactions(response.items)
    }
    setTableData({ ...tableData, meta: response })
  }

  const handleSkipCountChange = async (skipCount: boolean) => {
    const searchQuery = getQuery()

    const response = await walletsApiClient.getFileUploadHistoryTransactions({
      search: searchQuery,
      pageIndex: gridState.pageIndex,
      pageSize: gridState.take,
      skipCount,
    })
    const total = response.totalResults

    if (response?.items) {
      setTransactions(response.items)
    }
    gridActions.setGridFilters({
      total,
    })
    setTableData({ ...tableData, skipCount, meta: response })
  }
  const handleGridPageChange = async (page: number) => {
    const searchQuery = getQuery()
    const pageIndex = page
    gridActions.setGridFilters({
      pageIndex,
    })

    const response = await walletsApiClient.getFileUploadHistoryTransactions({
      search: searchQuery,
      pageIndex,
      pageSize: gridState.take,
      skipCount: tableData?.skipCount !== false,
    })
    const total = response.totalResults

    gridActions.setGridFilters({
      total,
    })
    if (response?.items) {
      setTransactions(response.items)
    }
    setTableData({ ...tableData, meta: response })
  }

  if (gridStatusState.isLoading) {
    return <Spinner />
  }

  return (
    <React.Fragment>
      <Breadcrumbs
        crumbs={[
          {
            title: 'Bulk Transactions - File Upload History',
            link: links.transactionBulkTransaction.url,
          },
        ]}
      />
      <TableStateContext.Provider value={[tableData, setTableData]}>
        <TableWithExport
          data={transactions}
          hasPermissions={hasPermission(permissions, PermissionsEnum.CanExportTransactionsModule)}
        >
          <Grid
            {...gridState}
            resizable
            data={transactions}
            sort={gridState.sort}
            total={gridState.total}
            take={gridState.take}
            skip={gridState.skip}
            pageable
            pager={() => (
              <Pager
                onSkipCountChange={handleSkipCountChange}
                onPageChange={handleGridPageChange}
                onPageSizeChange={handlePageSizeChange}
                page={gridState.pageIndex}
                pageSize={gridState.pageSize}
                take={gridState.take || 10}
                skip={gridState.skip || 0}
              />
            )}
          >
            <Column
              width={150}
              title='Filename'
              cell={(gridCellProps: GridCellProps) => {
                if (!gridCellProps.dataItem.fileName) {
                  return <td />
                }
                return (
                  <td
                    style={{ color: 'var(--link)', textDecoration: 'underline', cursor: 'pointer' }}
                    onClick={() =>
                      setUploadHistoryModal({
                        visible: true,
                        historyId: gridCellProps.dataItem.id,
                      })
                    }
                  >
                    {gridCellProps.dataItem.fileName}
                  </td>
                )
              }}
            />
            {transactionBulkTransactionFileUploadHistory
              .filter((c) => c.show)
              .map((column, idx) => (
                <Column key={idx + 2} {...column} />
              ))}
          </Grid>
        </TableWithExport>
      </TableStateContext.Provider>
      <Modal
        visible={uploadHistoryModal.visible}
        overflowY='auto'
        width={1100}
        onClose={() => setUploadHistoryModal({ historyId: undefined, visible: false })}
      >
        {uploadHistoryModal.historyId && (
          <UploadTransactionHistoryPage historyId={uploadHistoryModal.historyId} />
        )}
      </Modal>
    </React.Fragment>
  )
}

export default TransactionBulkTransactionFileUploadHistoryOverview
